import React, { useState, useEffect } from "react";
import { fetchDistributors, clearDistributors } from "../../features/distributor/DistributorSlice";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { RootState } from "../../store/store";
import { Search } from "lucide-react";
import { Loader2, AlertCircle, Settings, Bell } from "lucide-react";
import TopUpPage from "./TopUpPage";
import AddDistributorForm from "./AddDistributorPage";
import EditDistributorPage from "./EditDistributorPage";
import PurchaseOrdersPage from "./PurchaseOrdersPage";
import DistributorNotificationConfigPage from "./DistributorNotificationConfigPage";

/* --------------------------------- Interfaces ------------------------------ */

type Distributor = {
  distributorId: number;
  name: string;
};

type NotificationConfigNav = Distributor & {
  initialProgramId?: number;
};

const DistributorList: React.FC = () => {
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch();
  const { loading, error, distributorList } = useAppSelector((state: RootState) => state.distributor);

  /* --------------------------------- States ------------------------------ */

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
  const [addDistributor, setAddDistributor] = useState(false);
  const [editingDistributor, setEditingDistributor] = useState<number | null>(null);
  const [showOrdersForDistributor, setShowOrdersForDistributor] = useState(false);
  const [showTopUpForDistributor, setShowTopUpForDistributor] = useState(false);
  const [notificationConfigDistributor, setNotificationConfigDistributor] =
    useState<NotificationConfigNav | null>(null);
  /* --------------------------------- Hooks ------------------------------ */

  useEffect(() => {
    dispatch(fetchDistributors(null));
  }, [dispatch]);

  /* ------------------------------ Event Handlers ---------------------------- */

  const handleSearch = () => {
    dispatch(fetchDistributors(searchTerm));
  };

  const handleTopUp = (distributor: Distributor) => {
    setSelectedDistributor(distributor);
    setShowTopUpForDistributor(true);
  };

  const handleTopUpBack = async () => {
    setSelectedDistributor(null);
    setShowTopUpForDistributor(false);
    dispatch(fetchDistributors(null));
  };

  const handleAddDistributor = () => {
    setAddDistributor(true);
  };

  const handleAddDistributorBack = () => {
    setAddDistributor(false);
    dispatch(fetchDistributors(null));
  };

  const handleEdit = (distributorId: number) => {
    setEditingDistributor(distributorId);
  };

  const handleEditBack = () => {
    setEditingDistributor(null);
    dispatch(fetchDistributors(null));
  };

  const handleAlertSettings = (
    distributor: Distributor,
    initialProgramId?: number
  ) => {
    setNotificationConfigDistributor({ ...distributor, initialProgramId });
  };

  const handleAlertSettingsBack = () => {
    setNotificationConfigDistributor(null);
    dispatch(fetchDistributors(null));
  };

  const handleOrders = async (distributor: Distributor) => {
    setSelectedDistributor(distributor);
    setShowOrdersForDistributor(true);
  };
  const handleOrdersBack = () => {
    setShowOrdersForDistributor(false);
  };

  /* ------------------------------ UI Handlers ---------------------------- */

  if (addDistributor) {
    return <AddDistributorForm onBack={handleAddDistributorBack} />;
  }

  if (selectedDistributor && showTopUpForDistributor) {
    return (
      <TopUpPage
        distributor={selectedDistributor}
        onBack={handleTopUpBack}
        onOpenAlertSettings={(programId) => {
          setShowTopUpForDistributor(false);
          handleAlertSettings(selectedDistributor, programId);
        }}
      />
    );
  }

  if (notificationConfigDistributor) {
    return (
      <DistributorNotificationConfigPage
        distributor={notificationConfigDistributor}
        initialProgramId={notificationConfigDistributor.initialProgramId}
        onBack={handleAlertSettingsBack}
      />
    );
  }

  if (editingDistributor !== null) {
    return (
      <EditDistributorPage
        distributorId={editingDistributor}
        onBack={handleEditBack}
        onOpenAlertSettings={() => {
          const item = distributorList?.find((d) => d.distributorId === editingDistributor);
          if (item) {
            setEditingDistributor(null);
            handleAlertSettings({ distributorId: item.distributorId, name: item.name });
          }
        }}
      />
    );
  }

  if (selectedDistributor && showOrdersForDistributor) {
    return <PurchaseOrdersPage distributor={selectedDistributor} onBack={handleOrdersBack} />;
  }

  // Header content
  const header = (
    <div className="flex items-center gap-3">
      <h1 className="text-2xl font-bold text-gray-900">
        Distributors ({distributorList?.length || 0})
      </h1>
    </div>
  );

  const header_sidebar = (
    <div className="flex items-center gap-3">
      <div className="relative">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-4 pr-10 py-2 border border-cardbox rounded-md text-sm font-medium text-gray-500 bg-white focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition-colors"
        />
        <button
          onClick={handleSearch}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      <button
        onClick={handleAddDistributor}
        className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors flex items-center gap-2"
      >
        Add Distributor
      </button>
    </div>
  );

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center h-10">
          {header}
        </div>
        <div className="flex items-center h-10">
          {header_sidebar}
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 mb-8 lg:px-8">
        {/* Error State */}
        {error && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{error ? error : "Something went wrong!"}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        )}

        {/* Distributor List */}
        {!loading && !error && distributorList && (
          <div className="space-y-4">
            {distributorList.map((item) => (
              <div
                key={item.distributorId}
                onClick={() => handleEdit(item.distributorId)}
                className="bg-white rounded-xl p-6 flex flex-col md:flex-row md:items-center md:justify-between transition-shadow duration-200 transition-colors border-[1px] border-cardborder hover:shadow-md cursor-pointer"
              >
                {/* Left Section - Distributor Info */}
                <div className="flex-1 md:mb-0">
                  <div className="text-lg font-bold text-gray-900 mb-1">
                    {item.name}
                  </div>
                </div>

                {/* Right Section - Actions */}
                <div className="flex items-center gap-4">
                  {/* Orders Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOrders({
                        distributorId: item.distributorId,
                        name: item.name
                      });
                    }}
                    className={`px-4 py-2 text-sm font-medium border rounded-md focus:outline-none transition-colors ${item.allowPurchaseOrderCreation === false ? 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed' : 'text-brand border-brand hover:bg-brand hover:text-white'}`}
                    disabled={item.allowPurchaseOrderCreation === false}
                    title={item.allowPurchaseOrderCreation === false ? 'Purchase Orders are not allowed for this distributor' : ''}
                  >
                    Orders
                  </button>
                  {/* Top Up Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTopUp({
                        distributorId: item.distributorId,
                        name: item.name,
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand hover:text-white focus:outline-none transition-colors"
                  >
                    Top Up
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAlertSettings({
                        distributorId: item.distributorId,
                        name: item.name,
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none transition-colors flex items-center gap-1"
                  >
                    <Bell className="w-4 h-4" />
                    Alerts
                  </button>
                  {/* Settings Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(item.distributorId);
                    }}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-gray-50 hover:rounded-full rounded-md transition-colors"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {distributorList.length === 0 && (
              <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
                <div className="flex items-center gap-2 justify-center text-gray-500">
                  <span>No distributors found.</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DistributorList;
